/**
 * /app/settings — Change 9
 * Full settings page reachable via the gear icon in the top nav.
 * Sections: Account, Alerts, My places, My profile, Privacy & consent,
 *           App language, Help, Security.
 */
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  CheckCircle2,
  Globe,
  HelpCircle,
  Lock,
  MapPin,
  ShieldCheck,
  Trash2,
  User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { authApi, settingsApi, apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/settings")({
  head: () => ({ meta: [{ title: "Settings" }] }),
  component: SettingsPage,
});

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }: {
  title: string;
  icon?: typeof User;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2.5">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{title}</h2>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </section>
  );
}

function Row({ label, value, onTap, danger }: {
  label: string;
  value?: string;
  onTap?: () => void;
  danger?: boolean;
}) {
  if (onTap) {
    return (
      <button
        type="button"
        onClick={onTap}
        className={cn(
          "flex w-full items-center justify-between px-4 py-3 text-sm hover:bg-muted/40 transition-colors",
          danger && "text-severe",
        )}
      >
        <span className="font-medium">{label}</span>
        <div className="flex items-center gap-2 text-muted-foreground">
          {value && <span className="text-xs">{value}</span>}
          <ChevronRight className="h-4 w-4" />
        </div>
      </button>
    );
  }
  return (
    <div className="flex items-center justify-between px-4 py-3 text-sm">
      <span className="font-medium text-muted-foreground">{label}</span>
      {value && <span className="text-xs font-semibold">{value}</span>}
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, disabled }: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div>
        <p className="text-sm font-medium">{label}</p>
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Load profile + prefs ─────────────────────────────────────────────────
  const profileQ = useQuery({
    queryKey: ["settings", "profile"],
    queryFn: () => settingsApi.getProfile(),
    staleTime: 0,
  });
  const prefsQ = useQuery({
    queryKey: ["settings", "prefs"],
    queryFn: () => settingsApi.getPrefs(),
    staleTime: 0,
  });
  const placesQ = useQuery({
    queryKey: ["settings", "places"],
    queryFn: () => settingsApi.getPlaces(),
    staleTime: 0,
  });
  const sessionsQ = useQuery({
    queryKey: ["settings", "sessions"],
    queryFn: () => settingsApi.getSessions(),
    staleTime: 0,
  });

  const user = profileQ.data?.user;
  const prefs = prefsQ.data?.preferences;

  // ── Alert channel state ──────────────────────────────────────────────────
  const [sms, setSms] = useState(true);
  const [push, setPush] = useState(false);
  const [whatsapp, setWhatsapp] = useState(false);
  const [email, setEmail] = useState(true);
  const [alertLang, setAlertLang] = useState("en");
  const prefsDirty = useRef(false);

  useEffect(() => {
    if (!prefs || prefsDirty.current) return;
    if (typeof prefs.channelsSms === "boolean") setSms(prefs.channelsSms);
    if (typeof prefs.channelsPush === "boolean") setPush(prefs.channelsPush);
    if (typeof prefs.channelsWhatsapp === "boolean") setWhatsapp(prefs.channelsWhatsapp);
    if (typeof prefs.channelsEmail === "boolean") setEmail(prefs.channelsEmail);
    if (prefs.messageLanguage) setAlertLang(String(prefs.messageLanguage).toLowerCase());
  }, [prefs]);

  const savePrefs = useMutation({
    mutationFn: () =>
      settingsApi.patchPrefs({ channelsSms: sms, channelsPush: push, channelsWhatsapp: whatsapp, channelsEmail: email, messageLanguage: alertLang }),
    onSuccess: () => { prefsDirty.current = false; toast.success("Alert preferences saved"); void queryClient.invalidateQueries({ queryKey: ["settings", "prefs"] }); },
    onError: () => toast.error("Could not save preferences"),
  });

  // ── Send test alert ──────────────────────────────────────────────────────
  const [testSending, setTestSending] = useState(false);
  async function sendTestAlert() {
    setTestSending(true);
    try {
      await apiFetch("/me/test-alert", { method: "POST", audience: "user" });
      toast.success("Test alert sent — check your SMS");
    } catch {
      toast.error("Could not send test alert");
    } finally {
      setTestSending(false);
    }
  }

  // ── Delete place ─────────────────────────────────────────────────────────
  const deletePlace = useMutation({
    mutationFn: (id: string) => settingsApi.deletePlace(id),
    onSuccess: () => { toast.success("Place removed"); void queryClient.invalidateQueries({ queryKey: ["settings", "places"] }); },
    onError: () => toast.error("Could not remove place"),
  });

  // ── Sign out all ─────────────────────────────────────────────────────────
  async function signOutAll() {
    if (!confirm("Sign out of all devices? You will be logged out here too.")) return;
    await settingsApi.revokeAllSessions();
    await authApi.logoutUser();
    window.location.assign("/login");
  }

  // ── Privacy requests ─────────────────────────────────────────────────────
  async function requestExport() {
    if (!confirm("Request a copy of your data? We will email you within 30 days.")) return;
    try { await settingsApi.privacyRequest("EXPORT"); toast.success("Data export requested"); }
    catch { toast.error("Could not submit request"); }
  }
  async function requestDeletion() {
    if (!confirm("Delete your account and all data? This cannot be undone after processing.")) return;
    try {
      await settingsApi.privacyRequest("DELETION");
      toast.success("Account deletion requested. You will be signed out.");
      setTimeout(() => void authApi.logoutUser().then(() => window.location.assign("/login")), 2000);
    } catch { toast.error("Could not submit deletion request"); }
  }

  // ── App language ─────────────────────────────────────────────────────────
  function setUiLang(lang: "en" | "sw") {
    void i18n.changeLanguage(lang);
    try { localStorage.setItem("anga_ui_lang", lang); } catch { /* ignore */ }
  }

  // ── Version ──────────────────────────────────────────────────────────────
  const appVersion = String(import.meta.env["VITE_APP_VERSION"] || "1.0.0");

  const hasPhone = Boolean(user?.phone);

  return (
    <div className="space-y-5 pb-6">
      {/* Back link */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void navigate({ to: "/app/home" })}
          className="text-xs font-semibold text-primary hover:underline"
        >
          ← Back to Home
        </button>
      </div>

      <h1 className="font-display text-2xl font-bold">Settings</h1>

      {/* ── Account ──────────────────────────────────────────────────────── */}
      <Section title="Account" icon={User}>
        <Row label="Name" value={String(user?.name ?? "—")} />
        <Row label="Email" value={user?.email ? String(user.email) : "Not set"} />
        <div className="flex items-center justify-between px-4 py-3 text-sm">
          <span className="font-medium">Phone</span>
          <div className="flex items-center gap-2 text-xs">
            {user?.phone ? (
              <>
                <span className="font-mono">{String(user.phone)}</span>
                {user.phoneVerified ? (
                  <span className="flex items-center gap-0.5 text-success font-semibold">
                    <CheckCircle2 className="h-3 w-3" /> Verified
                  </span>
                ) : (
                  <span className="text-warning font-semibold">Unverified</span>
                )}
              </>
            ) : (
              <Link to="/app/phone-gate" className="font-semibold text-primary hover:underline">
                Add phone
              </Link>
            )}
          </div>
        </div>
        <Row label="Sign-in method" value={user?.authProvider === "GOOGLE" ? "Google" : user?.authProvider === "PHONE" ? "Phone / OTP" : "Email & password"} />
        {user?.authProvider === "PASSWORD" && (
          <Row label="Change password" onTap={() => void navigate({ to: "/forgot-password" })} />
        )}
      </Section>

      {/* ── Alerts ───────────────────────────────────────────────────────── */}
      <Section title="Alerts" icon={ShieldCheck}>
        <p className="px-4 py-2 text-[11px] text-muted-foreground">
          You receive all alerts for your places at any time. Choose how we reach you.
        </p>
        <ToggleRow label="SMS" checked={sms} onChange={(v) => { prefsDirty.current = true; setSms(v); }} />
        <ToggleRow label="Push notifications" checked={push} onChange={(v) => { prefsDirty.current = true; setPush(v); }} />
        <ToggleRow
          label="WhatsApp"
          hint={!hasPhone ? "Add a phone number first" : "Counts as explicit opt-in to WhatsApp alerts"}
          checked={whatsapp && hasPhone}
          disabled={!hasPhone}
          onChange={(v) => { if (!hasPhone) return; prefsDirty.current = true; setWhatsapp(v); }}
        />
        <ToggleRow label="Email" checked={email} onChange={(v) => { prefsDirty.current = true; setEmail(v); }} />

        {/* Alert language */}
        <div className="px-4 py-3">
          <p className="mb-2 text-xs font-semibold">Alert language</p>
          <div className="flex gap-2">
            {(["en", "sw"] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => { prefsDirty.current = true; setAlertLang(lang); }}
                className={cn(
                  "flex-1 rounded-md border py-2 text-sm font-semibold transition-colors",
                  alertLang === lang ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground",
                )}
              >
                {lang === "en" ? "English" : "Kiswahili"}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 pb-3">
          <Button
            className="w-full"
            variant="secondary"
            disabled={savePrefs.isPending || prefsQ.isLoading}
            onClick={() => void savePrefs.mutateAsync()}
          >
            {savePrefs.isPending ? "Saving…" : "Save alert preferences"}
          </Button>
        </div>

        {/* Test alert */}
        <div className="border-t border-border px-4 py-3">
          <p className="mb-1 text-xs font-semibold">Send me a test alert</p>
          <p className="mb-2 text-[11px] text-muted-foreground">Sends a TEST-labelled SMS. Max 3 per day.</p>
          <Button
            variant="outline"
            className="w-full"
            disabled={testSending || !sms || !hasPhone}
            onClick={() => void sendTestAlert()}
          >
            {testSending ? "Sending…" : "Send test SMS"}
          </Button>
        </div>
      </Section>

      {/* ── My places ────────────────────────────────────────────────────── */}
      <Section title="My places" icon={MapPin}>
        {placesQ.isLoading ? (
          <div className="px-4 py-3 text-sm text-muted-foreground">Loading…</div>
        ) : placesQ.data?.places?.length ? (
          placesQ.data.places.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <div>
                <p className="font-medium">{String(p.label)}</p>
                <p className="text-[11px] text-muted-foreground">{[p.ward, p.county].filter(Boolean).join(", ")}</p>
              </div>
              <button
                type="button"
                aria-label={`Remove ${p.label}`}
                onClick={() => { if (confirm(`Remove "${p.label}"?`)) deletePlace.mutate(p.id); }}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-severe"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        ) : (
          <div className="px-4 py-3 text-sm text-muted-foreground">No saved places.</div>
        )}
        <div className="px-4 py-3">
          <Link to="/app/search" className="text-xs font-semibold text-primary hover:underline">
            + Add a place via Search →
          </Link>
        </div>
      </Section>

      {/* ── My profile ───────────────────────────────────────────────────── */}
      <Section title="My profile" icon={User}>
        <div className="px-4 py-3 text-sm">
          <p className="font-medium mb-1">Persona</p>
          <p className="text-muted-foreground">
            {Array.isArray(user?.personas)
              ? (user.personas as string[]).join(", ")
              : String(user?.personas ?? "resident")}
          </p>
          <Link to="/onboarding/persona" className="mt-2 block text-xs font-semibold text-primary hover:underline">
            Edit persona →
          </Link>
        </div>
      </Section>

      {/* ── Privacy & consent ────────────────────────────────────────────── */}
      <Section title="Privacy & consent" icon={ShieldCheck}>
        <Row label="Review consent choices" onTap={() => void navigate({ to: "/onboarding/consent" })} />
        <Row label="Download my data" onTap={() => void requestExport()} />
        <Row label="Delete my account" onTap={() => void requestDeletion()} danger />
      </Section>

      {/* ── App language ─────────────────────────────────────────────────── */}
      <Section title="App language" icon={Globe}>
        <div className="flex gap-2 px-4 py-3">
          {(["en", "sw"] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => setUiLang(lang)}
              className={cn(
                "flex-1 rounded-md border py-2 text-sm font-semibold transition-colors",
                i18n.language === lang ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground",
              )}
            >
              {lang === "en" ? "English" : "Kiswahili"}
            </button>
          ))}
        </div>
      </Section>

      {/* ── Help ─────────────────────────────────────────────────────────── */}
      <Section title="Help" icon={HelpCircle}>
        <Row label="Privacy policy" onTap={() => window.open("/privacy", "_blank")} />
        <Row label="SMS Policy" onTap={() => window.open("/privacy#sms", "_blank")} />
        <Row label="Terms of service" onTap={() => window.open("/privacy#terms", "_blank")} />
        <Row label="Contact support" onTap={() => window.open("mailto:support@meteo.co.ke", "_blank")} />
        <Row label="App version" value={appVersion} />
      </Section>

      {/* ── Security ─────────────────────────────────────────────────────── */}
      <Section title="Security" icon={Lock}>
        <div className="px-4 py-3">
          <p className="mb-2 text-xs font-semibold">Active sessions</p>
          {sessionsQ.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : sessionsQ.data?.sessions?.length ? (
            <div className="space-y-2">
              {sessionsQ.data.sessions.map((s, i) => (
                <div key={s.id} className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px]">
                  <p className="font-semibold">{i === 0 ? "This device" : `Device ${i + 1}`}</p>
                  <p className="text-muted-foreground">{s.userAgent ? String(s.userAgent).slice(0, 60) : "Unknown browser"}</p>
                  <p className="text-muted-foreground">
                    IP: {s.ip ? String(s.ip) : "—"} · Since {new Date(s.createdAt).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No active sessions found.</p>
          )}
        </div>
        <div className="border-t border-border px-4 py-3">
          <Button variant="outline" className="w-full text-severe hover:text-severe" onClick={() => void signOutAll()}>
            Sign out of all devices
          </Button>
        </div>
      </Section>
    </div>
  );
}
